import React, { useEffect, useMemo, useState } from "react";
import {
  WorkshopCostItem,
  OrderWorkshopCost,
  CalculationType,
  User,
} from "../types";
import { costService } from "../services/dataService";
import { formatNumber } from "../utils/formatters";
import AlertModal from "./AlertModal";
import ConfirmModal from "./ConfirmModal";
import "./WorkshopTransferModal.css";

/**
 * Atölye transfer modalı.
 *
 * Sipariş bir atölyeden diğerine geçerken, ESKİ atölyede kullanılan maliyet
 * kalemleri burada girilir; ardından transfer yapılır. Maliyet girişi isteğe
 * bağlıdır — kalem seçilmeden de transfer edilebilir.
 *
 * Çoğunlukla QR ile mobilde açıldığı için mobilde alttan açılan sayfa (sheet),
 * masaüstünde ortalı pencere olarak çizilir. Kalem giriş formu ayrı bir
 * pencere DEĞİL, aynı sayfanın içinde ikinci bir görünümdür: mobilde üst üste
 * üç pencere açmak kullanılmaz hâle geliyordu.
 */

type CostPayload = Omit<
  OrderWorkshopCost,
  | "orderWorkshopCostId"
  | "createdAt"
  | "createdBy"
  | "updatedAt"
  | "updatedBy"
  | "order"
  | "workshop"
  | "costItem"
>;

interface WorkshopTransferModalProps {
  isOpen: boolean;
  orderId: string;
  orderQuantity?: number;
  oldWorkshopId: string | null;
  oldWorkshopName: string;
  newWorkshopId: string | null;
  newWorkshopName: string;
  currentUser?: User;
  onClose: () => void;
  onSave: (costs: CostPayload[]) => Promise<void>;
}

/** Tek bir kalem için girilen değerler. */
interface CostInput {
  costItemId: string;
  calculationType: CalculationType;
  quantityUsed: number | "";
  quantity2?: number | "";
  quantity3?: number;
  totalCost?: number | "";
  unit: string;
  unit2?: string;
  unit3?: string;
  costUnitId3?: string;
  quantity1Label?: string;
  quantity2Label?: string;
  actualPrice: number;
  currency: string;
  notes: string;
}

// Hesaplama tipine göre kaç miktar alanı sorulur (sipariş adedi hariç)
const paramCount = (t: CalculationType): number => {
  switch (t) {
    case CalculationType.CustomCost:
      return 0;
    case CalculationType.Simple:
    case CalculationType.MeterBased:
      return 1;
    default:
      return 2;
  }
};

// Sipariş adedini kendiliğinden hesaba katan tipler
const usesOrderQty = (t: CalculationType) =>
  t === CalculationType.PieceFitting ||
  t === CalculationType.MeterBased ||
  t === CalculationType.AreaBased;

const num = (v: number | "" | undefined) => (v === "" || v === undefined ? 0 : v);

/** Kaydedilecek toplam — eski modaldaki formülün aynısı. */
const lineTotal = (i: CostInput): number => {
  if (i.calculationType === CalculationType.CustomCost) return num(i.totalCost);
  // Boya hesaplamada ikinci miktar (çeşit sayısı) bilgi amaçlıdır, çarpana girmez
  const second = i.calculationType === CalculationType.PaintBased ? 1 : num(i.quantity2) || 1;
  return num(i.quantityUsed) * second * i.actualPrice;
};

const isValid = (i: CostInput): boolean => {
  if (i.calculationType === CalculationType.CustomCost) return num(i.totalCost) > 0;
  if (num(i.quantityUsed) <= 0) return false;
  if (paramCount(i.calculationType) >= 2 && num(i.quantity2) <= 0) return false;
  return true;
};

const I = {
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  back: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 12 5 5L20 7" />
    </svg>
  ),
  plus: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  star: (
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="m12 2 3 6.6 7 .8-5.2 4.8 1.4 7L12 17.8 5.8 21.2l1.4-7L2 9.4l7-.8z" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  ),
};

const WorkshopTransferModal: React.FC<WorkshopTransferModalProps> = ({
  isOpen,
  orderId,
  orderQuantity,
  oldWorkshopId,
  oldWorkshopName,
  newWorkshopName,
  currentUser,
  onClose,
  onSave,
}) => {
  const [items, setItems] = useState<WorkshopCostItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [done, setDone] = useState<Map<string, CostInput>>(new Map());
  const [editing, setEditing] = useState<{ item: WorkshopCostItem; input: CostInput } | null>(null);

  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "warning" | "error" }>({
    isOpen: false,
    message: "",
    type: "warning",
  });
  const [confirm, setConfirm] = useState<{ isOpen: boolean; message: string }>({ isOpen: false, message: "" });

  // İlk atama: eski atölye yok → maliyet sorulmaz, yalnız onay alınır
  const hasOldWorkshop =
    !!oldWorkshopId && oldWorkshopId !== "null" && oldWorkshopId !== "undefined";
  const isFirstAssignment = !hasOldWorkshop || oldWorkshopName === "Atanmamış İşler";

  useEffect(() => {
    if (!isOpen) return;
    setDone(new Map());
    setEditing(null);
    setSearch("");
    setCategory("all");
    if (!hasOldWorkshop) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const list = await costService.getWorkshopCostItems(oldWorkshopId!);
        if (cancelled) return;
        setItems(
          list
            .filter((x) => (x.isActive === undefined || x.isActive) && !!x.costItemId)
            .sort((a, b) => (a.priority || 999) - (b.priority || 999)),
        );
      } catch {
        if (!cancelled) setItems([]); // kalem yüklenemezse transfer yine yapılabilir
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, oldWorkshopId]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((x) => x.categoryName).filter(Boolean) as string[])).sort(),
    [items],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (x) =>
        !done.has(x.costItemId) &&
        (!q || (x.costItemName || "").toLowerCase().includes(q)) &&
        (category === "all" || x.categoryName === category),
    );
  }, [items, done, search, category]);

  // Para birimine göre toplam — farklı birimler tek sayıya toplanmaz
  const totals = useMemo(() => {
    const m = new Map<string, number>();
    done.forEach((i) => m.set(i.currency, (m.get(i.currency) || 0) + lineTotal(i)));
    return Array.from(m.entries());
  }, [done]);

  // ---- Kalem giriş formu ----

  const openItem = (item: WorkshopCostItem, existing?: CostInput) => {
    if (existing) return setEditing({ item, input: existing });
    const t = item.calculationType ?? item.costItem?.calculationType ?? CalculationType.Simple;
    const n = paramCount(t);
    const withQty = usesOrderQty(t);
    setEditing({
      item,
      input: {
        costItemId: item.costItemId,
        calculationType: t,
        quantityUsed: "",
        quantity2: n >= 2 ? "" : undefined,
        quantity3: withQty ? orderQuantity || 0 : undefined,
        totalCost: t === CalculationType.CustomCost ? "" : undefined,
        unit: item.unitName || item.unitCode || "Birim",
        unit2: n >= 2 ? item.unitName2 || "Birim 2" : undefined,
        unit3: withQty ? "Sipariş Adedi" : undefined,
        costUnitId3: item.costUnitId3,
        quantity1Label: item.quantity1Label || item.costItem?.quantity1Label,
        quantity2Label: item.quantity2Label || item.costItem?.quantity2Label,
        actualPrice: item.effectivePrice,
        currency: item.effectiveCurrency || item.standardCurrency || "TRY",
        notes: "",
      },
    });
  };

  const patch = (p: Partial<CostInput>) =>
    setEditing((e) => (e ? { ...e, input: { ...e.input, ...p } } : e));

  const commitItem = () => {
    if (!editing) return;
    const i = editing.input;
    if (!isValid(i)) {
      setAlert({
        isOpen: true,
        type: "warning",
        message:
          i.calculationType === CalculationType.CustomCost
            ? "Toplam tutar sıfırdan büyük olmalı."
            : paramCount(i.calculationType) >= 2 && num(i.quantity2) <= 0
              ? `${i.quantity2Label || i.unit2 || "İkinci miktar"} girilmeli.`
              : "Miktar sıfırdan büyük olmalı.",
      });
      return;
    }
    setDone((d) => new Map(d).set(i.costItemId, i));
    setEditing(null);
  };

  const removeItem = (id: string) =>
    setDone((d) => {
      const n = new Map(d);
      n.delete(id);
      return n;
    });

  // ---- Kaydet ----

  const askConfirm = () => {
    if (editing) {
      setAlert({ isOpen: true, type: "warning", message: "Açık kalem girişini kaydedin ya da vazgeçin." });
      return;
    }
    const n = done.size;
    setConfirm({
      isOpen: true,
      message: isFirstAssignment
        ? `${newWorkshopName} atölyesine atama yapılacak.\n\nOnaylıyor musunuz?`
        : `${oldWorkshopName} → ${newWorkshopName}${n > 0 ? `\n\n${n} maliyet kalemi kaydedilecek.` : "\n\nMaliyet kalemi girilmedi."}\n\nOnaylıyor musunuz?`,
    });
  };

  const performSave = async () => {
    setConfirm({ isOpen: false, message: "" });
    try {
      setSaving(true);
      // İlk atamada maliyet olmaz; eski modalla aynı kural
      const costs: CostPayload[] = isFirstAssignment
        ? []
        : Array.from(done.values()).map((i) => {
            const custom = i.calculationType === CalculationType.CustomCost;
            return {
              orderId,
              workshopId: oldWorkshopId || "",
              costItemId: i.costItemId,
              quantityUsed: custom ? 0 : num(i.quantityUsed),
              quantity2: custom ? undefined : num(i.quantity2) || undefined,
              quantity3: custom ? undefined : i.quantity3,
              unit: i.unit || "Birim",
              unit2: custom ? undefined : i.unit2,
              unit3: custom ? undefined : i.unit3,
              costUnitId3: custom ? undefined : i.costUnitId3,
              actualPrice: custom ? 0 : i.actualPrice,
              currency: i.currency,
              totalCost: lineTotal(i),
              notes: i.notes.trim() || undefined,
              isActive: true,
            };
          });
      await onSave(costs);
      setDone(new Map());
      setEditing(null);
    } catch (e: any) {
      setAlert({ isOpen: true, type: "error", message: e?.message || "İşlem başarısız oldu." });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const primaryLabel = saving
    ? "Kaydediliyor…"
    : isFirstAssignment
      ? "Atamayı Yap"
      : done.size > 0
        ? `${done.size} kalemle transfer et`
        : currentUser?.role === "Modelist"
          ? "Transfer Et"
          : "Maliyetsiz devam et";

  return (
    <div className="wt-overlay" onClick={saving ? undefined : onClose}>
      <div className="wt-sheet" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="wt-grip" aria-hidden />

        {/* ===== Başlık ===== */}
        <header className="wt-head">
          {editing ? (
            <button className="wt-iconbtn" onClick={() => setEditing(null)} aria-label="Geri" type="button">
              {I.back}
            </button>
          ) : (
            <span className="wt-kicker">Atölye transferi</span>
          )}
          <button className="wt-iconbtn" onClick={onClose} disabled={saving} aria-label="Kapat" type="button">
            {I.close}
          </button>
        </header>

        {/* ===== Rota ===== */}
        {!editing && (
          <div className="wt-route">
            <div className="wt-route-box">
              <span className="lbl">Mevcut</span>
              <span className="val">{oldWorkshopName}</span>
            </div>
            <span className="wt-route-arrow">{I.arrow}</span>
            <div className="wt-route-box next">
              <span className="lbl">Hedef</span>
              <span className="val">{newWorkshopName}</span>
            </div>
          </div>
        )}

        {/* ===== Gövde ===== */}
        <div className="wt-body">
          {editing ? (
            <ItemForm
              item={editing.item}
              input={editing.input}
              onChange={patch}
            />
          ) : isFirstAssignment ? (
            <div className="wt-note">
              {I.info}
              <div>
                <b>İlk atama</b>
                Sipariş henüz bir atölyede çalışılmadı; maliyet girişi gerekmiyor. Onayladığınızda
                doğrudan <b>{newWorkshopName}</b> atölyesine atanır.
              </div>
            </div>
          ) : loading ? (
            <div className="wt-state">
              <span className="wt-spin" />
              Maliyet kalemleri yükleniyor…
            </div>
          ) : (
            <>
              {/* Girilen kalemler */}
              {done.size > 0 && (
                <section className="wt-sec">
                  <h4>
                    Girilen maliyetler <span className="wt-count">{done.size}</span>
                  </h4>
                  <ul className="wt-done">
                    {Array.from(done.values()).map((i) => {
                      const it = items.find((x) => x.costItemId === i.costItemId);
                      if (!it) return null;
                      return (
                        <li key={i.costItemId}>
                          <span className="wt-done-check">{I.check}</span>
                          <button type="button" className="wt-done-main" onClick={() => openItem(it, i)}>
                            <span className="t">{it.costItemName}</span>
                            <span className="m">
                              {i.calculationType === CalculationType.CustomCost
                                ? "Sabit tutar"
                                : `${num(i.quantityUsed)} ${i.unit}${
                                    i.unit2 ? ` × ${num(i.quantity2)} ${i.unit2}` : ""
                                  }`}
                              {i.notes ? ` · ${i.notes}` : ""}
                            </span>
                          </button>
                          <span className="wt-done-total">
                            {formatNumber(lineTotal(i))} {i.currency}
                          </span>
                          <button
                            type="button"
                            className="wt-iconbtn sm danger"
                            aria-label="Kaldır"
                            onClick={() => removeItem(i.costItemId)}
                          >
                            {I.close}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {/* Kalem seçimi */}
              <section className="wt-sec">
                <h4>
                  {oldWorkshopName} kalemleri{" "}
                  {done.size === 0 && <span className="wt-hint">isteğe bağlı</span>}
                </h4>

                {items.length === 0 ? (
                  <div className="wt-empty">
                    Bu atölye için tanımlı maliyet kalemi yok. Maliyet girmeden devam edebilirsiniz.
                  </div>
                ) : (
                  <>
                    <label className="wt-search">
                      {I.search}
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Kalem ara…"
                        inputMode="search"
                      />
                    </label>

                    {categories.length > 1 && (
                      <div className="wt-chips">
                        <button
                          type="button"
                          className={`wt-chip ${category === "all" ? "on" : ""}`}
                          onClick={() => setCategory("all")}
                        >
                          Tümü
                        </button>
                        {categories.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`wt-chip ${category === c ? "on" : ""}`}
                            onClick={() => setCategory(c)}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    )}

                    {visible.length === 0 ? (
                      <div className="wt-empty">
                        {done.size > 0 && !search && category === "all"
                          ? "Tüm kalemler girildi."
                          : "Eşleşen kalem yok."}
                      </div>
                    ) : (
                      <ul className="wt-list">
                        {visible.map((it) => (
                          <li key={it.workshopCostItemId}>
                            <button type="button" className="wt-item" onClick={() => openItem(it)}>
                              <span className="wt-item-main">
                                <span className="t">
                                  {it.costItemName}
                                  {it.isPreferred && (
                                    <span className="wt-star" title="Tercih edilen">
                                      {I.star}
                                    </span>
                                  )}
                                </span>
                                <span className="m">
                                  {it.categoryName || "Kategori yok"}
                                  {it.effectivePrice > 0 &&
                                    ` · ${formatNumber(it.effectivePrice)} ${
                                      it.effectiveCurrency || it.standardCurrency || "TRY"
                                    } / ${it.unitName || it.unitCode || "birim"}`}
                                </span>
                              </span>
                              <span className="wt-item-add">{I.plus}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </section>
            </>
          )}
        </div>

        {/* ===== Alt çubuk ===== */}
        <footer className="wt-foot">
          {editing ? (
            <>
              <button type="button" className="wt-btn" onClick={() => setEditing(null)}>
                Vazgeç
              </button>
              <button
                type="button"
                className="wt-btn primary"
                onClick={commitItem}
                disabled={!isValid(editing.input)}
              >
                {I.check}
                Kalemi ekle
              </button>
            </>
          ) : (
            <>
              {totals.length > 0 && (
                <div className="wt-foot-sum">
                  <span className="lbl">Toplam</span>
                  <span className="val">
                    {totals.map(([c, v]) => `${formatNumber(v)} ${c}`).join(" · ")}
                  </span>
                </div>
              )}
              <button type="button" className="wt-btn" onClick={onClose} disabled={saving}>
                İptal
              </button>
              <button type="button" className="wt-btn primary" onClick={askConfirm} disabled={saving}>
                {!saving && I.arrow}
                {primaryLabel}
              </button>
            </>
          )}
        </footer>
      </div>

      <AlertModal
        isOpen={alert.isOpen}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert((a) => ({ ...a, isOpen: false }))}
      />
      <ConfirmModal
        isOpen={confirm.isOpen}
        message={confirm.message}
        type="info"
        confirmText="Onayla"
        cancelText="İptal"
        onConfirm={performSave}
        onCancel={() => setConfirm({ isOpen: false, message: "" })}
      />
    </div>
  );
};

/* ---- Kalem giriş formu (sayfanın içinde, ayrı pencere değil) ---- */

const ItemForm: React.FC<{
  item: WorkshopCostItem;
  input: CostInput;
  onChange: (p: Partial<CostInput>) => void;
}> = ({ item, input, onChange }) => {
  const custom = input.calculationType === CalculationType.CustomCost;
  const paint = input.calculationType === CalculationType.PaintBased;
  const two = paramCount(input.calculationType) >= 2;
  const total = lineTotal(input);

  const toNum = (v: string): number | "" => (v === "" ? "" : parseFloat(v));

  return (
    <div className="wt-form">
      <div className="wt-form-head">
        <span className="wt-form-cat">{item.categoryName || "Maliyet kalemi"}</span>
        <h3>{item.costItemName}</h3>
        {!custom && (
          <span className="wt-form-price">
            {formatNumber(input.actualPrice)} {input.currency} / {input.unit}
          </span>
        )}
      </div>

      {custom ? (
        <>
          <div className="wt-note compact">
            {I.info}
            <div>
              <b>Sabit tutar</b>
              Bu kalemde miktar ve birim fiyat yok; toplam tutar doğrudan girilir.
            </div>
          </div>
          <label className="wt-field">
            <span>
              Toplam tutar <em>{input.currency}</em>
            </span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              autoFocus
              value={input.totalCost ?? ""}
              onChange={(e) => onChange({ totalCost: toNum(e.target.value) })}
              placeholder="0,00"
            />
          </label>
        </>
      ) : (
        <>
          <div className={`wt-form-grid ${two ? "two" : ""}`}>
            <label className="wt-field">
              <span>
                {input.quantity1Label || "Miktar"} <em>{input.unit}</em>
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                autoFocus
                value={input.quantityUsed}
                onChange={(e) => onChange({ quantityUsed: toNum(e.target.value) })}
                placeholder="0"
              />
            </label>
            {two && (
              <label className="wt-field">
                <span>
                  {input.quantity2Label || input.unit2} {!paint && <em>{input.unit2}</em>}
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={input.quantity2 ?? ""}
                  onChange={(e) => onChange({ quantity2: toNum(e.target.value) })}
                  placeholder="0"
                />
              </label>
            )}
          </div>

          {paint && <p className="wt-help">Çeşit sayısı bilgi amaçlıdır, hesaplamaya katılmaz.</p>}
          {input.unit3 && (
            <p className="wt-help">
              Sipariş adedi ({input.quantity3}) hesaba kendiliğinden katılır.
            </p>
          )}
        </>
      )}

      <label className="wt-field">
        <span>Not</span>
        <textarea
          rows={2}
          value={input.notes}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="İsteğe bağlı"
        />
      </label>

      <div className="wt-form-total">
        <span>Kalem toplamı</span>
        <b>
          {formatNumber(total)} {input.currency}
        </b>
      </div>
    </div>
  );
};

export default WorkshopTransferModal;
